import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormat } from '../../i18n/format.js';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Shield,
  FileText,
  Wrench,
  Car,
  AlertCircle,
  Filter,
  X,
  RefreshCw,
  Download,
  Fuel,
  Clock,
  Plus,
  FileDown,
  ChevronDown,
} from 'lucide-react';
import { calendarAPI } from '../../api/calendar.js';
import { API_BASE_URL, getAuthHeaders } from '../../api/core.js';

// Category config — single source of truth for colors, icons (labels resolved via t() at render)
const CATEGORIES = [
  { value: 'all', color: 'gray' },
  { value: 'insurance', color: 'blue' },
  { value: 'claims', color: 'red' },
  { value: 'document', color: 'purple' },
  { value: 'maintenance', color: 'red' },
  { value: 'inspection', color: 'orange' },
  { value: 'trip', color: 'indigo' },
  { value: 'resource', color: 'teal' },
  { value: 'financing', color: 'violet' },
  { value: 'custom', color: 'gray' },
];

const CATEGORY_ICONS = {
  insurance: Shield,
  claims: AlertCircle,
  document: FileText,
  maintenance: Wrench,
  inspection: AlertCircle,
  trip: Car,
  resource: Fuel,
  financing: Clock,
  delivery: Car,
  custom: CalendarIcon,
};

const COLOR_CLASSES = {
  blue: { bg: 'bg-blue-100 text-blue-800', pill: 'bg-blue-50 border-blue-500 hover:bg-blue-100', text: 'text-blue-600', gradient: 'from-blue-500 to-blue-600' },
  purple: { bg: 'bg-purple-100 text-purple-800', pill: 'bg-purple-50 border-purple-500 hover:bg-purple-100', text: 'text-purple-600', gradient: 'from-purple-500 to-purple-600' },
  green: { bg: 'bg-green-100 text-green-800', pill: 'bg-green-50 border-green-500 hover:bg-green-100', text: 'text-green-600', gradient: 'from-green-500 to-green-600' },
  amber: { bg: 'bg-amber-100 text-amber-800', pill: 'bg-amber-50 border-amber-500 hover:bg-amber-100', text: 'text-amber-600', gradient: 'from-amber-500 to-amber-600' },
  red: { bg: 'bg-red-100 text-red-800', pill: 'bg-red-50 border-red-500 hover:bg-red-100', text: 'text-red-600', gradient: 'from-red-500 to-red-600' },
  indigo: { bg: 'bg-indigo-100 text-indigo-800', pill: 'bg-indigo-50 border-indigo-500 hover:bg-indigo-100', text: 'text-indigo-600', gradient: 'from-indigo-500 to-indigo-600' },
  teal: { bg: 'bg-teal-100 text-teal-800', pill: 'bg-teal-50 border-teal-500 hover:bg-teal-100', text: 'text-teal-600', gradient: 'from-teal-500 to-teal-600' },
  violet: { bg: 'bg-violet-100 text-violet-800', pill: 'bg-violet-50 border-violet-500 hover:bg-violet-100', text: 'text-violet-600', gradient: 'from-violet-500 to-violet-600' },
  orange: { bg: 'bg-orange-100 text-orange-800', pill: 'bg-orange-50 border-orange-500 hover:bg-orange-100', text: 'text-orange-600', gradient: 'from-orange-500 to-orange-600' },
};
const DEFAULT_COLOR = { bg: 'bg-gray-100 text-gray-800', pill: 'bg-gray-50 border-gray-500 hover:bg-gray-100', text: 'text-gray-600', gradient: 'from-gray-500 to-gray-600' };

function CalendarView({ onMessage }) {
  const { t, fmt, locale } = useFormat();
  const queryClient = useQueryClient();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Single data source: calendar_events table via API
  const { data: calendarEvents = [], isLoading } = useQuery({
    queryKey: ['calendarEvents'],
    queryFn: async () => {
      const response = await calendarAPI.getEvents();
      return response.data || [];
    },
    staleTime: 30000,
  });

  // Parse events into renderable format
  const events = useMemo(() => {
    return calendarEvents.map(ev => ({
      ...ev,
      date: new Date(ev.start_date),
      color: ev.color || 'gray',
    }));
  }, [calendarEvents]);

  // Filter
  const filteredEvents = useMemo(() => {
    if (categoryFilter === 'all') return events;
    return events.filter(e => e.category === categoryFilter);
  }, [events, categoryFilter]);

  // Sync: project events from source entities into calendar_events table
  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await calendarAPI.sync();
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
      if (onMessage) {
        onMessage({
          title: t('calendar.messages.syncSuccessTitle'),
          message: result.message || t('calendar.messages.syncSuccessDefault', {
            created: result.summary?.created || 0,
            updated: result.summary?.updated || 0,
          }),
          type: 'success',
        });
      }
    } catch (err) {
      if (onMessage) {
        onMessage({ title: t('calendar.messages.syncErrorTitle'), message: err.message, type: 'error' });
      }
    } finally {
      setSyncing(false);
    }
  }, [queryClient, onMessage, t]);

  // ICS export
  const handleExport = useCallback(async () => {
    try {
      const authHeaders = getAuthHeaders();
      const params = categoryFilter !== 'all' ? `?category=${categoryFilter}` : '';
      const url = `${API_BASE_URL}/calendar/export.ics${params}`;
      console.log('[Calendar] ICS export URL:', url, 'headers:', Object.keys(authHeaders));
      const response = await fetch(url, {
        method: 'GET',
        headers: { ...authHeaders },
      });
      console.log('[Calendar] ICS response status:', response.status, 'type:', response.headers.get('content-type'));
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Export failed (${response.status}): ${errText.substring(0, 100)}`);
      }
      const text = await response.text();
      console.log('[Calendar] ICS content length:', text.length);
      // Force download via Blob with explicit MIME type
      const blob = new Blob([text], { type: 'text/calendar;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = blobUrl;
      a.download = 'fleetmanager_calendar.ics';
      document.body.appendChild(a);
      a.click();
      // Cleanup after short delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
      }, 200);
      if (onMessage) onMessage({ title: t('calendar.messages.exportIcsTitle'), message: t('calendar.messages.exportIcsSuccess', { size: Math.round(text.length / 1024) }), type: 'success' });
    } catch (err) {
      console.error('[Calendar] ICS export error:', err);
      if (onMessage) onMessage({ title: t('calendar.messages.exportIcsErrorTitle'), message: err.message, type: 'error' });
    }
  }, [categoryFilter, onMessage, t]);

  // Calendar navigation
  const previousMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  const goToToday = () => setCurrentDate(new Date());

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const getEventsForDate = (date) => {
    return filteredEvents.filter(event => {
      const d = event.date;
      return d.getDate() === date.getDate() && d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
    });
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 border border-gray-200 bg-gray-50"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayEvents = getEventsForDate(date);
      const isToday = date.toDateString() === new Date().toDateString();
      const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();

      days.push(
        <div
          key={day}
          onClick={() => setSelectedDate(date)}
          className={`h-24 border border-gray-200 p-1 cursor-pointer transition-colors ${
            isToday ? 'bg-blue-50 border-blue-400' : 'bg-white hover:bg-gray-50'
          } ${isSelected ? 'ring-2 ring-amber-500' : ''}`}
        >
          <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
            {day}
          </div>
          <div className="space-y-0.5 overflow-hidden">
            {dayEvents.slice(0, 2).map(event => (
              <div
                key={event.id}
                className={`text-xs px-1 py-0.5 rounded truncate ${(COLOR_CLASSES[event.color] || DEFAULT_COLOR).bg}`}
              >
                {event.title}
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div className="text-xs text-gray-500 px-1">+{dayEvents.length - 2}</div>
            )}
          </div>
        </div>
      );
    }
    return days;
  };

  // Weekday header labels — from Intl via the active locale, never a hardcoded key
  // (2023-01-01 is a Sunday; the grid always starts the week on Sunday, see getFirstDayOfMonth's getDay()).
  const weekdayLabels = useMemo(
    () => Array.from({ length: 7 }, (_, i) => fmt.date(new Date(2023, 0, 1 + i), { weekday: 'short' })),
    [locale] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const getEventIcon = (category) => {
    const Icon = CATEGORY_ICONS[category] || CalendarIcon;
    return <Icon className="w-4 h-4" />;
  };

  const categoryLabel = (code) => t(`calendar.categories.${code}`, { defaultValue: code?.replace('_', ' ') });
  const priorityLabel = (code) => t(`calendar.priority.${code}`, { defaultValue: code });
  const statusLabel = (code) => t(`calendar.status.${code}`, { defaultValue: code });
  const eventTypeLabel = (code) => (code ? t(`calendar.eventType.${code}`, { defaultValue: code.replace('_', ' ') }) : t('common.states.notAvailable'));
  const sourceEntityLabel = (code) => t(`calendar.sourceEntity.${code}`, { defaultValue: code });
  const sourceFieldLabel = (code) => t(`calendar.sourceField.${code}`, { defaultValue: code });

  // PDF export
  const handlePDFExport = useCallback(async (range) => {
    try {
      const authHeaders = getAuthHeaders();
      const url = `${API_BASE_URL}/calendar/export.pdf?range=${range}`;
      const response = await fetch(url, { headers: { ...authHeaders } });
      if (!response.ok) throw new Error(t('calendar.messages.exportFailed', { status: response.status }));
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = blobUrl;
      a.download = `calendrier_${range}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(blobUrl); document.body.removeChild(a); }, 200);
      if (onMessage) onMessage({ title: t('calendar.messages.exportPdfTitle'), message: t('calendar.messages.exportPdfSuccess'), type: 'success' });
    } catch (err) {
      if (onMessage) onMessage({ title: t('calendar.messages.exportPdfErrorTitle'), message: err.message, type: 'error' });
    }
  }, [onMessage, t]);

  const [showPdfMenu, setShowPdfMenu] = useState(false);

  // Month events for the right column — sorted by date
  const monthEvents = useMemo(() => {
    return filteredEvents
      .filter(e =>
        e.date.getMonth() === currentDate.getMonth() &&
        e.date.getFullYear() === currentDate.getFullYear()
      )
      .sort((a, b) => a.date - b.date);
  }, [filteredEvents, currentDate]);

  // Group month events by day
  const eventsByDay = useMemo(() => {
    const groups = {};
    for (const ev of monthEvents) {
      const key = ev.date.toISOString().slice(0, 10);
      if (!groups[key]) groups[key] = { date: ev.date, events: [] };
      groups[key].events.push(ev);
    }
    return groups;
  }, [monthEvents]);

  return (
    <div className="p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CalendarIcon className="w-8 h-8 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('calendar.title')}</h1>
            <p className="text-sm text-gray-600">
              {t('calendar.header.eventsCount', { count: filteredEvents.length })}
              {' — '}
              {t('calendar.header.thisMonthCount', { count: monthEvents.length })}
              {filteredEvents.filter(e => e.priority === 'high' || e.priority === 'critical').length > 0 && (
                <span className="text-red-600 ml-2 font-medium">
                  {t('calendar.header.priorityCount', { count: filteredEvents.filter(e => e.priority === 'high' || e.priority === 'critical').length })}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
            title={t('calendar.actions.syncTooltip')}
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? t('calendar.actions.syncing') : t('calendar.actions.sync')}
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            title={t('calendar.actions.exportIcsTooltip')}
          >
            <Download className="w-4 h-4" />
            {t('calendar.actions.exportIcs')}
          </button>
          {/* PDF export dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowPdfMenu(!showPdfMenu)}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              title={t('calendar.actions.exportPdfTooltip')}
            >
              <FileDown className="w-4 h-4" />
              {t('calendar.actions.exportPdf')}
              <ChevronDown className="w-3 h-3" />
            </button>
            {showPdfMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[200px]">
                <button onClick={() => { handlePDFExport('week'); setShowPdfMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700">
                  {t('calendar.pdfMenu.week')}
                </button>
                <button onClick={() => { handlePDFExport('next_week'); setShowPdfMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700">
                  {t('calendar.pdfMenu.nextWeek')}
                </button>
                <button onClick={() => { handlePDFExport('2weeks'); setShowPdfMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700">
                  {t('calendar.pdfMenu.twoWeeks')}
                </button>
                <div className="border-t border-gray-100 my-1"></div>
                <button onClick={() => { handlePDFExport('month'); setShowPdfMenu(false); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-gray-700 font-medium">
                  {t('calendar.pdfMenu.month')}
                </button>
              </div>
            )}
          </div>
          <button
            onClick={goToToday}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            {t('calendar.today')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">{t('calendar.filters.show')}</span>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat.value}
                onClick={() => setCategoryFilter(cat.value)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  categoryFilter === cat.value
                    ? `bg-${cat.color}-500 text-white`
                    : `bg-${cat.color}-100 text-${cat.color}-700 hover:bg-${cat.color}-200`
                }`}
              >
                {t(`calendar.categories.${cat.value}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-8 text-gray-500">{t('calendar.loadingCalendar')}</div>
      )}

      {/* Two-column layout: Calendar + Event List */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* LEFT: Calendar Grid */}
        <div>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <button onClick={previousMonth} className="p-2 hover:bg-amber-400 rounded-lg transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-bold capitalize">
                  {fmt.date(currentDate, 'monthYear')}
                </h2>
                <button onClick={nextMonth} className="p-2 hover:bg-amber-400 rounded-lg transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-7 gap-0 mb-2">
                {weekdayLabels.map((day, i) => (
                  <div key={i} className="text-center text-sm font-semibold text-gray-700 py-2 capitalize">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0">
                {renderCalendar()}
              </div>
            </div>
          </div>

          {/* Selected date detail below calendar */}
          {selectedDate && selectedDateEvents.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-4 mt-4">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                {fmt.date(selectedDate, { weekday: 'long', day: 'numeric', month: 'long' })}
              </h3>
              <div className="space-y-2">
                {selectedDateEvents.map(event => {
                  const colors = COLOR_CLASSES[event.color] || DEFAULT_COLOR;
                  return (
                    <div
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className={`p-3 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-md ${colors.pill}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`mt-0.5 ${colors.text}`}>
                          {getEventIcon(event.category)}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900">{event.title}</div>
                          {event.description && (
                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">{event.description}</div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500 capitalize">{categoryLabel(event.category)}</span>
                            {(event.priority === 'high' || event.priority === 'critical') && (
                              <span className="text-xs text-red-700 font-medium">
                                {priorityLabel(event.priority)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Detailed Event List for the month */}
        <div>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold capitalize">
                  {t('calendar.list.title', { month: fmt.date(currentDate, 'monthYear') })}
                </h2>
                <span className="text-sm opacity-80">{t('calendar.list.count', { count: monthEvents.length })}</span>
              </div>
            </div>

            <div className="divide-y divide-gray-100 max-h-[calc(100vh-280px)] overflow-y-auto">
              {Object.keys(eventsByDay).length === 0 ? (
                <div className="text-center py-12">
                  <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">{t('calendar.list.emptyMonth')}</p>
                </div>
              ) : (
                Object.entries(eventsByDay).map(([dayKey, { date: dayDate, events: dayEvents }]) => (
                  <div key={dayKey}>
                    {/* Day header */}
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 sticky top-0">
                      <span className="text-sm font-semibold text-gray-700 capitalize">{fmt.date(dayDate, { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                      <span className="text-xs text-gray-500 ml-2">({dayEvents.length})</span>
                    </div>
                    {/* Events for this day */}
                    {dayEvents.map(event => {
                      const colors = COLOR_CLASSES[event.color] || DEFAULT_COLOR;
                      return (
                        <div
                          key={event.id}
                          onClick={() => setSelectedEvent(event)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`mt-0.5 p-1.5 rounded ${colors.bg}`}>
                              {getEventIcon(event.category)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-900 truncate">{event.title}</span>
                                {(event.priority === 'high' || event.priority === 'critical') && (
                                  <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${
                                    event.priority === 'critical' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                                  }`}>
                                    {priorityLabel(event.priority)}
                                  </span>
                                )}
                              </div>
                              {event.description && (
                                <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{event.description}</p>
                              )}
                              <div className="flex items-center gap-3 mt-1">
                                <span className={`text-xs px-1.5 py-0.5 rounded ${colors.bg}`}>
                                  {categoryLabel(event.category)}
                                </span>
                                {event.status && (
                                  <span className="text-xs text-gray-500">
                                    {statusLabel(event.status)}
                                  </span>
                                )}
                                {event.source_entity && (
                                  <span className="text-xs text-gray-400">
                                    {sourceEntityLabel(event.source_entity)} #{event.source_id}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Close PDF menu when clicking outside */}
      {showPdfMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowPdfMenu(false)} />
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className={`px-6 py-4 bg-gradient-to-r ${(COLOR_CLASSES[selectedEvent.color] || DEFAULT_COLOR).gradient} text-white`}>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="text-white">{getEventIcon(selectedEvent.category)}</div>
                  <div>
                    <h2 className="text-xl font-bold">{selectedEvent.title}</h2>
                    <p className="text-sm opacity-90">
                      {categoryLabel(selectedEvent.category)} — {fmt.date(selectedEvent.date, 'long')}
                    </p>
                  </div>
                </div>
                <button onClick={() => setSelectedEvent(null)} className="text-white hover:text-gray-200 transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{t('calendar.detail.title')}</h3>
                {selectedEvent.description && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">{t('calendar.detail.description')}</label>
                    <p className="text-sm text-gray-900">{selectedEvent.description}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">{t('calendar.detail.category')}</label>
                    <p className="text-sm text-gray-900 capitalize">{categoryLabel(selectedEvent.category)}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">{t('calendar.detail.type')}</label>
                    <p className="text-sm text-gray-900 capitalize">{eventTypeLabel(selectedEvent.event_type)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500">{t('calendar.detail.priority')}</label>
                    <p className="text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        selectedEvent.priority === 'critical' ? 'bg-red-100 text-red-800' :
                        selectedEvent.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        selectedEvent.priority === 'low' ? 'bg-gray-100 text-gray-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {priorityLabel(selectedEvent.priority)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">{t('calendar.detail.status')}</label>
                    <p className="text-sm">
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                        {statusLabel(selectedEvent.status)}
                      </span>
                    </p>
                  </div>
                </div>
                {selectedEvent.end_date && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">{t('calendar.detail.endDate')}</label>
                    <p className="text-sm text-gray-900">{fmt.date(selectedEvent.end_date)}</p>
                  </div>
                )}
                {selectedEvent.remind_days_before && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">{t('calendar.detail.reminder')}</label>
                    <p className="text-sm text-gray-900">{t('calendar.detail.reminderDays', { count: selectedEvent.remind_days_before })}</p>
                  </div>
                )}
                {selectedEvent.source_entity && (
                  <div className="pt-2 border-t border-gray-200">
                    <label className="text-xs font-medium text-gray-500">{t('calendar.detail.source')}</label>
                    <p className="text-sm text-gray-600">
                      {sourceEntityLabel(selectedEvent.source_entity)} #{selectedEvent.source_id}
                      {selectedEvent.source_field && ` (${sourceFieldLabel(selectedEvent.source_field?.replace(/:-?\d+$/, ''))})`}
                    </p>
                  </div>
                )}
                {selectedEvent.tags && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">{t('calendar.detail.tags')}</label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedEvent.tags.split(',').map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs">{tag.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setSelectedEvent(null)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                {t('common.actions.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarView;
